// Dashboard.js
import React, { useEffect, useRef, useState } from "react";
import MaxWidthWrapper from "../components/max-width-warpper";
import { cn } from "../utils/cn";
import { GoGrabber } from "react-icons/go";
import { constants } from "../utils/constants";
import { 
  getBookmarks, 
  createBookmark, 
  updateBookmark, 
  deleteBookmark, 
  updateGroup, 
  createGroup, 
  deleteGroup,
  moveBookmark 
} from "../service/authenticatedApiService";
import { formatUrl } from "../utils/urlFormatter";
import { useAuth } from "../contexts/AuthContext";
import ModalBox from "../components/modal-box";
import ContextMenu from "../components/context-menu";
import Forbidden from "../components/forbidden";
import EmptyGroup from "../components/empty-group";
import SkeltonLoading from "../components/skelton-loding";
import DropArea from "../components/drop-area";
import Error from "../components/error";
import Favicon from "../components/favicon";
import ProfileDropdown from "../components/profile-dropdown";

const Dashboard = () => {
  const { user, signOut, loading: authLoading } = useAuth();
  const [bookmarks, setBookmarks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalProps, setModalProps] = useState({});
  const [error, setError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [contextMenu, setContextMenu] = useState({
    isVisible: false,
    x: 0,
    y: 0,
    options: [],
  });
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isForbidden, setIsForbidden] = useState(false);
  const [hasFetchedBookmarks, setHasFetchedBookmarks] = useState(false);
  const [activeBookmark, setActiveBookmark] = useState(null);
  const [fetchingError, setFetchingError] = useState(false);

  useEffect(() => {
    // Only fetch bookmarks when auth is not loading, user is available, and we haven't fetched yet
    if (!authLoading && user && !hasFetchedBookmarks) {
      handleFetchBookmarks(true);
      setHasFetchedBookmarks(true);
    }
  }, [authLoading, user, hasFetchedBookmarks]);

  // Check for new_bookmark URL parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const newBookmark = urlParams.get('new_bookmark');
    
    if (newBookmark === 'true' && !showModal && bookmarks.length > 0) {
      // Show create bookmark modal with first group selected by default
      const firstGroup = bookmarks[0]?.group;
      if (firstGroup) {
        handleActions(
          constants.ACTION_ADD_ITEM,
          firstGroup.id,
          firstGroup.name
        );
        // Clean up URL parameter after showing modal
        const newUrl = new URL(window.location);
        newUrl.searchParams.delete('new_bookmark');
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, [bookmarks, showModal]);

  // Reset fetch flag when user changes (logout/login)
  useEffect(() => {
    if (!user) {
      setHasFetchedBookmarks(false);
      setBookmarks([]);
    }
  }, [user]);

  const handleFetchBookmarks = async (loading = false) => {
    setFetchingError(false);
    try {
      loading && setIsLoading(true);
      let data = await getBookmarks();
      if (data?.status === constants.SUCCESS) {
        setBookmarks(data?.bookmarks || []);
      } else if (data.status === constants.FORBIDDEN) {
        setIsForbidden(true);
      } else {
        setFetchingError(true);
      }
    } catch (error) {
      console.error("Error fetching bookmarks:", error);
      setFetchingError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleActions = (
    action,
    groupId = "",
    groupName = "",
    bookmark = null
  ) => {
    setModalProps({
      action,
      groupId,
      groupName,
      bookmark,
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalProps({});
    setError(null);
  };

  const handleCreateAndEdit = async (action, name, link, group_id, id) => {
    try {
      setIsModalLoading(true);
      const formattedUrl = formatUrl(link);
      
      if (action === constants.ACTION_ADD_ITEM) {
        const bookmark = { title: name, url: formattedUrl, group_id };
        let data = await createBookmark(bookmark);
        if (data?.status === constants.SUCCESS) {
          setShowModal(false);
          handleFetchBookmarks();
        } else {
          setError(constants.COMMON_ERROR);
        }
      } else {
        const bookmark = { title: name, url: formattedUrl, group_id, id };
        let data = await updateBookmark(bookmark);
        if (data?.status === constants.SUCCESS) {
          setShowModal(false);
          handleFetchBookmarks();
        } else {
          setError(constants.COMMON_ERROR);
        }
      }
    } catch (error) {
      setError(constants.COMMON_ERROR);
    } finally {
      setIsModalLoading(false);
    }
  };

  const handleDelete = async (action, groupId, id) => {
    try {
      setIsModalLoading(true);
      if (action === constants.ACTION_DELETE_ITEM) {
        let data = await deleteBookmark(groupId, id);
        if (data?.status === constants.SUCCESS) {
          setShowModal(false);
          handleFetchBookmarks();
        } else {
          setError(constants.COMMON_ERROR);
        }
      } else {
        setError(constants.COMMON_ERROR);
      }
    } catch (error) {
      setError(constants.COMMON_ERROR);
    } finally {
      setIsModalLoading(false);
    }
  };

  const handleRightClick = (event, group_id, title, bookmark) => {
    event.preventDefault();
    // Close any existing menu first
    setContextMenu({ ...contextMenu, isVisible: false });
    
    // Small delay to ensure the close happens before opening new menu
    setTimeout(() => {
      setContextMenu({
        isVisible: true,
        x: event.clientX,
        y: event.clientY,
        options: [
          {
            label: "Open",
            onClick: () => window.open(bookmark?.url, "_blank"),
          },
          {
            label: "Copy",
            onClick: () => {
              bookmark?.url
                ? navigator.clipboard.writeText(bookmark?.url)
                : navigator.clipboard.writeText(bookmark?.title);
            },
          },
          {
            label: "Edit",
            onClick: () =>
              handleActions(
                constants.ACTION_EDIT_ITEM,
                group_id,
                title,
                bookmark
              ),
          },

          {
            label: "Delete",
            onClick: () =>
              handleActions(
                constants.ACTION_DELETE_ITEM,
                group_id,
                title,
                bookmark
              ),
          },
          // Add more options as needed
        ],
      });
    }, 100);
  };

  // for group name
  const handleGroupRightClick = (event, group_id, title) => {
    event.preventDefault();
    // Close any existing menu first
    setContextMenu({ ...contextMenu, isVisible: false });
    
    // Small delay to ensure the close happens before opening new menu
    setTimeout(() => {
      setContextMenu({
        isVisible: true,
        x: event.clientX,
        y: event.clientY,
        options: [
          {
            label: "Edit",
            onClick: () =>
              handleActions(constants.ACTION_EDIT_GROUP_NAME, group_id, title),
          },
          {
            label: "Delete",
            onClick: () =>
              handleActions(constants.ACTION_DELETE_GROUP, group_id, title),
          },
          {
            label: "New Group",
            onClick: () => handleActions(constants.ACTION_ADD_GROUP),
          },
          {
            label: "New Bookmark",
            onClick: () =>
              handleActions(constants.ACTION_ADD_ITEM, group_id, title),
          },
          // Add more options as needed
        ],
      });
    }, 100);
  };

  const handleCreateAndEditGroupName = async (action, group_id, name) => {
    try {
      setIsModalLoading(true);
      if (action === constants.ACTION_EDIT_GROUP_NAME) {
        const group = { name, group_id };
        let data = await updateGroup(group);
        if (data?.status === constants.SUCCESS) {
          setShowModal(false);
          handleFetchBookmarks();
        } else {
          setError(constants.COMMON_ERROR);
        }
      } else if (action === constants.ACTION_ADD_GROUP) {
        let data = await createGroup(name);
        if (data?.status === constants.SUCCESS) {
          setShowModal(false);
          handleFetchBookmarks();
        } else {
          setError(constants.COMMON_ERROR);
        }
      }
    } catch (error) {
      setError(constants.COMMON_ERROR);
    } finally {
      setIsModalLoading(false);
    }
  };

  const handleDeleteGroup = async (action, groupId) => {
    try {
      setIsModalLoading(true);
      if (action === constants.ACTION_DELETE_GROUP) {
        let data = await deleteGroup(groupId);
        if (data?.status === constants.SUCCESS) {
          setShowModal(false);
          handleFetchBookmarks();
        } else {
          setError(constants.COMMON_ERROR);
        }
      } else {
        setError(constants.COMMON_ERROR);
      }
    } catch (error) {
      setError(constants.COMMON_ERROR);
    } finally {
      setIsModalLoading(false);
    }
  };

  const handleClickOutside = () => {
    setContextMenu({ ...contextMenu, isVisible: false });
  };

  const handleScrollOption = () => {
    setScrollPosition(window.scrollY);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScrollOption);
    return () => {
      window.removeEventListener("scroll", handleScrollOption);
    };
  }, []);

  const onDrop = (from, to, id, position) => {
    console.log(from, to, id, position);
    moveBookmarkLocally(from, to, id, position);
  };

  const moveBookmarkLocally = (fromGroupId, toGroupId, bookmarkId, position) => {
    const data = bookmarks;
    // Find the fromGroup and toGroup
    const fromGroup = data.find((item) => item.group.id === fromGroupId);
    const toGroup = data.find((item) => item.group.id === toGroupId);

    if (!fromGroup || !toGroup) {
      console.error('MoveBookmark: Could not find source or target group');
      return;
    }

    // Find the bookmark in the fromGroup
    const bookmarkIndex = fromGroup.bookmarks.findIndex(
      (bm) => bm.id === bookmarkId
    );

    if (bookmarkIndex === -1) {
      console.error('MoveBookmark: Could not find bookmark to move');
      return;
    }

    // Remove the bookmark from the fromGroup
    const [bookmark] = fromGroup.bookmarks.splice(bookmarkIndex, 1);

    // Insert the bookmark into the toGroup at the specified position
    toGroup.bookmarks.splice(position, 0, bookmark);

    // Return the modified data as a JSON string
    setBookmarks([...data]); // Use spread operator to trigger re-render
    handleMoveBookmarkInDB(fromGroupId, toGroupId, bookmarkId, position);
  };

  const handleMoveBookmarkInDB = async (
    fromGroupId,
    toGroupId,
    bookmarkId,
    position
  ) => {
    try {
      let data = await moveBookmark(fromGroupId, toGroupId, bookmarkId, position);
      if (data.status === constants.SUCCESS) {
        handleFetchBookmarks();
      } else {
        setFetchingError(true);
      }
    } catch (error) {
      setFetchingError(true);
    }
  };

  if (isForbidden && !isLoading) return <Forbidden />;

  if (!bookmarks.length > 0 && !isLoading && !isForbidden && !fetchingError && !showModal)
    return <EmptyGroup handleActions={handleActions} />;

  if (isLoading||authLoading) return <SkeltonLoading />;

  if (!isLoading && fetchingError&&!authLoading)
    return <Error handleFetch={handleFetchBookmarks} />;

  // console.log(JSON.stringify(bookmarks));

  return (
    <MaxWidthWrapper>
      <>
        {/* Header with user info and logout */}
        <div className="w-full flex justify-between items-center my-10">
          <div className="flex-1"></div>
          <h2 className="font-bold text-[50px] text-center text-zinc-500/20 select-none">
            Bookmarks
          </h2>
          <div className="flex-1 flex justify-end items-center">
            {user && (
              <ProfileDropdown user={user} onSignOut={signOut} />
            )}
          </div>
        </div>
        <div className="w-full h-full  grid lg:grid-cols-3 sm:grid-cols-2 gap-y-10 gap-x-3">
          {bookmarks?.map(({ group, bookmarks }, i) => {
            const title = group?.name || 'Untitled Group';
            const group_id = group?.id;
            return (
            <div key={group_id} className="w-full h-full bg-zinc-900/20">
              <div>
                <div
                  onContextMenu={(event) => {
                    handleGroupRightClick(event, group_id, title);
                  }}
                  onClick={() => {
                    activeIndex === -1 || activeIndex !== i
                      ? setActiveIndex(i)
                      : setActiveIndex(-1);
                  }}
                  className="w-full flex cursor-pointer justify-between bg-zinc-900/70 border border-zinc-800 border-dashed py-3 px-10 border-r border-l"
                >
                  <h4
                    className={cn(
                      "text-center capitalize text-base text-zinc-500 font-medium truncate hover:text-clip"
                    )}
                  >
                    {title}
                  </h4>
                </div>
                <DropArea
                  onDrop={() => {
                    onDrop(
                      activeBookmark.groupId,
                      group_id,
                      activeBookmark.id,
                      0
                    );
                  }}
                />
                {bookmarks.map((bookmark, i) => (
                  <>
                    <div
                      onClick={() => {
                        window.open(bookmark?.url, "_blank");
                      }}
                      onContextMenu={(event) =>
                        handleRightClick(event, group_id, title, bookmark)
                      }
                      key={bookmark.id}
                      className="font-mono capitalize w-full odd:bg-zinc-800/10 cursor-pointer"
                    >
                      <ul>
                        <li
                          draggable
                          onDragStart={() => {
                            setActiveBookmark({
                              groupId: group_id,
                              id: bookmark.id,
                            });
                          }}
                          onDragEnd={() => {
                            setActiveBookmark(null);
                          }}
                          className={cn(
                            "border flex items-center justify-between font-semibold w-full text-zinc-400 text-[15px] border-dashed border-zinc-800 p-3",
                            activeBookmark?.id === bookmark.id && "border-white"
                          )}
                        >
                          <div className="flex items-center">
                            <GoGrabber className="text-zinc-400 mr-3 w-6 h-6 bg-zinc-900 rounded-md cursor-grab" />
                            {bookmark?.title}
                          </div>
                          <Favicon 
                            url={bookmark?.url} 
                            name={bookmark?.title} 
                            favicon={bookmark?.favicon}
                            className="w-5 h-5"
                          />
                        </li>
                      </ul>
                      <DropArea
                        onDrop={() => {
                          onDrop(
                            activeBookmark.groupId,
                            group_id,
                            activeBookmark.id,
                            i + 1
                          );
                        }}
                      />
                    </div>
                  </>
                ))}
                {!bookmarks.length > 0 && (
                  <div
                    onClick={() => {
                      handleActions(constants.ACTION_ADD_ITEM, group_id, title);
                    }}
                    className="flex px-2 py-4 border border-dashed border-t-0 border-zinc-800 cursor-pointer"
                  >
                    <p className="text-sm font-bold font-mono">
                      Empty. Click hear to Create Bookmark
                    </p>
                  </div>
                )}
              </div>
            </div>
            );
          })}
        </div>
      </>

      <ModalBox
        showModal={showModal}
        props={modalProps}
        handleClose={handleCloseModal}
        handleCreateAndEdit={handleCreateAndEdit}
        handleCreateAndEditGroupName={handleCreateAndEditGroupName}
        handleDelete={handleDelete}
        handleDeleteGroup={handleDeleteGroup}
        setError={setError}
        error={error}
        isModalLoading={isModalLoading}
        groups={bookmarks?.map(({ group }) => group) || []}
      />
      <ContextMenu {...contextMenu} onClose={handleClickOutside} />
    </MaxWidthWrapper>
  );
};

export default Dashboard;
